import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Conversation, Message, ConversationsResponse } from 'models/ai-chat/ai-chat-response';
import { ConversationsState, ConversationState } from 'models/ai-chat/chat.types';
import { FactDimTables, TablePreviewInfo } from 'models/ai-chat/data-explorer';
import { Project } from 'api/services/ai-chat-api-service';

const initialState: ConversationsState = {
  byId: {},
  allIds: [],
  activeConversationId: null,
  loading: false,
  error: null,
  conversations: [],
  dataExplorer: false,
  factDimTables: null,
  tablePreviewData: null,
  loadingTable: false,
  loadingTablePreview: false,
  // Pagination fields
  totalCount: 0,
  pageSize: 10,
  currentPage: 1,
  hasMore: false,
  // Project fields
  projects: [],
  visibleProjects: [],
  loadingProjects: false,
  projectsError: null,
  activeProject: null,
  projectsLoaded: false,
  // Project conversations state
  activeProjectConversationList: [],
  loadingProjectConversations: false,
  projectConversationsError: null,
};

const conversationsSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    setActiveConversation: (state, action: PayloadAction<string | null>) => {
      state.activeConversationId = action.payload;
    },
    
    addConversation: (state, action: PayloadAction<ConversationState>) => {
      const { id } = action.payload;
      state.byId[id] = action.payload;
      if (!state.allIds.includes(id)) {
        state.allIds.push(id);
      }
      state.activeConversationId = id;
    },
    
    updateConversation: (state, action: PayloadAction<{ id: string; messages: Message[] }>) => {
      const { id, messages } = action.payload;
      if (state.byId[id]) {
        state.byId[id].messages = messages.map((msg: Message) => ({
          ...msg,
          pending: false,
          error: false,
        }));
        state.byId[id].lastUpdated = new Date().toISOString();
      }
    },
    
    addMessageToConversation: (state, action: PayloadAction<{ id: string; message: Message }>) => {
      const { id, message } = action.payload;
      if (state.byId[id]) {
        state.byId[id].messages.push({
          ...message,
          pending: false,
          error: false,
        });
        state.byId[id].lastUpdated = new Date().toISOString();
      }
    },
    
    // New method to set conversations manually
    setConversations: (state, action: PayloadAction<ConversationsResponse>) => {
      const { conversations, total_count, page_size, page } = action.payload;
      
      // Reset state
      state.byId = {};
      state.allIds = [];
      state.conversations = conversations;
      
      // Update pagination fields
      state.totalCount = total_count;
      state.pageSize = page_size;
      state.currentPage = page;
      state.hasMore = page * page_size < total_count;
      
      // Add conversations
      conversations.forEach((conversation: Conversation) => {
        state.byId[conversation.conversation_id] = {
          id: conversation.conversation_id,
          title: conversation.title,
          messages: [], // Conversations from list don't include full messages
          lastUpdated: conversation.updated_at,
        };
        state.allIds.push(conversation.conversation_id);
      });
    },

    // New method to append conversations for pagination
    appendConversations: (state, action: PayloadAction<ConversationsResponse>) => {
      const { conversations, total_count, page_size, page } = action.payload;
      
      // Update pagination fields
      state.totalCount = total_count;
      state.pageSize = page_size;
      state.currentPage = page;
      state.hasMore = page * page_size < total_count;
      
      // Append conversations (don't reset existing ones)
      conversations.forEach((conversation: Conversation) => {
        // Only add if not already present
        if (!state.byId[conversation.conversation_id]) {
          state.byId[conversation.conversation_id] = {
            id: conversation.conversation_id,
            title: conversation.title,
            messages: [], // Conversations from list don't include full messages
            lastUpdated: conversation.updated_at,
          };
          state.allIds.push(conversation.conversation_id);
        }
      });
      
      // Append to conversations array, avoiding duplicates
      conversations.forEach((conversation: Conversation) => {
        if (!state.conversations.find(c => c.conversation_id === conversation.conversation_id)) {
          state.conversations.push(conversation);
        }
      });
    },
    
    // New method to set a single conversation manually
    setNewConversation: (state, action: PayloadAction<Conversation>) => {
      const conversation = action.payload;
      
      state.byId[conversation.conversation_id] = {
        id: conversation.conversation_id,
        title: conversation.title,
        messages: [], // New conversations start with empty messages
        lastUpdated: conversation.updated_at,
      };
      
      if (!state.allIds.includes(conversation.conversation_id)) {
        state.allIds.push(conversation.conversation_id);
      }

      state.conversations = [conversation, ...state.conversations]; 
      state.activeConversationId = conversation.conversation_id;
    },
    
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setDataExplorer: (state, action: PayloadAction<boolean>) => {
      state.dataExplorer = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setFactDimTables: (state, action: PayloadAction<FactDimTables[] | null>) => {
      state.factDimTables = action.payload;
    },
    setTablePreview: (state, action: PayloadAction<TablePreviewInfo | null>) => {
      state.tablePreviewData = action.payload;
    },
    setLoadingTable: (state, action: PayloadAction<boolean>) => {
      state.loadingTable = action.payload;
    },
    setLoadingTablePreview: (state, action: PayloadAction<boolean>) => {
      state.loadingTablePreview = action.payload;
    },

    // Project reducers
    setProjects: (state, action: PayloadAction<Project[]>) => {
      state.projects = action.payload;
      state.visibleProjects = action.payload.slice(0, 2); // Show first 2 projects
      state.projectsLoaded = true;
    },
    setVisibleProjects: (state, action: PayloadAction<Project[]>) => {
      state.visibleProjects = action.payload;
    },
    addProject: (state, action: PayloadAction<Project>) => {
      state.projects.unshift(action.payload); // Add to beginning
      state.visibleProjects = state.projects.slice(0, 2); // Update visible projects
      state.projectsLoaded = true;
    },
    setLoadingProjects: (state, action: PayloadAction<boolean>) => {
      state.loadingProjects = action.payload;
    },
    setProjectsLoadAttempted: (state) => {
      state.projectsLoaded = true; // Mark as attempted, even if failed
    },
    setProjectsError: (state, action: PayloadAction<string | null>) => {
      state.projectsError = action.payload;
    },
    setActiveProject: (state, action: PayloadAction<Project | null>) => {
      state.activeProject = action.payload;
    },
    loadMoreProjects: (state) => {
      const currentVisible = state.visibleProjects.length;
      const nextVisible = Math.min(currentVisible + 2, state.projects.length);
      state.visibleProjects = state.projects.slice(0, nextVisible);
    },
    // Project conversations reducers
    setActiveProjectConversationList: (state, action: PayloadAction<Conversation[]>) => {
      state.activeProjectConversationList = action.payload;
    },
    setLoadingProjectConversations: (state, action: PayloadAction<boolean>) => {
      state.loadingProjectConversations = action.payload;
    },
    setProjectConversationsError: (state, action: PayloadAction<string | null>) => {
      state.projectConversationsError = action.payload;
    },
    clearProjectConversations: (state) => {
      state.activeProjectConversationList = [];
      state.projectConversationsError = null;
    },
    
    // Project management actions
    deleteProject: (state, action: PayloadAction<string>) => {
      const projectId = action.payload;
      state.projects = state.projects.filter(p => p.projectId !== projectId);
      state.visibleProjects = state.visibleProjects.filter(p => p.projectId !== projectId);
      // Clear active project if it was the deleted one
      if (state.activeProject?.projectId === projectId) {
        state.activeProject = null;
        state.activeProjectConversationList = [];
      }
    },
    
    renameProject: (state, action: PayloadAction<{projectId: string, newName: string}>) => {
      const { projectId, newName } = action.payload;
      // Update in main projects array
      const projectIndex = state.projects.findIndex(p => p.projectId === projectId);
      if (projectIndex !== -1) {
        state.projects[projectIndex].projectName = newName;
      }
      // Update in visible projects array
      const visibleProjectIndex = state.visibleProjects.findIndex(p => p.projectId === projectId);
      if (visibleProjectIndex !== -1) {
        state.visibleProjects[visibleProjectIndex].projectName = newName;
      }
      // Update active project if it matches
      if (state.activeProject?.projectId === projectId) {
        state.activeProject.projectName = newName;
      }
    },
    
    // Conversation management actions  
    deleteConversationFromState: (state, action: PayloadAction<string>) => {
      const conversationId = action.payload;
      // Remove from main conversations list
      state.conversations = state.conversations.filter(c => c.conversation_id !== conversationId);
      // Remove from active project conversations if present
      state.activeProjectConversationList = state.activeProjectConversationList.filter(c => c.conversation_id !== conversationId);
      // Clear active conversation if it was the deleted one
      if (state.activeConversationId === conversationId) {
        state.activeConversationId = null;
      }
    }

  }
});

export const {
  setActiveConversation,
  addConversation,
  updateConversation,
  addMessageToConversation,
  setConversations,
  appendConversations,
  setNewConversation,
  setLoading,
  setError,
  setDataExplorer,
  setFactDimTables,
  setTablePreview,
  setLoadingTable,
  setLoadingTablePreview,
  // Project actions
  setProjects,
  setVisibleProjects,
  addProject,
  setLoadingProjects,
  setProjectsLoadAttempted,
  setProjectsError,
  setActiveProject,
  loadMoreProjects,
  // Project conversations actions
  setActiveProjectConversationList,
  setLoadingProjectConversations,
  setProjectConversationsError,
  clearProjectConversations,
  // Project management actions
  deleteProject,
  renameProject,
  // Conversation management actions
  deleteConversationFromState
} = conversationsSlice.actions;

export default conversationsSlice.reducer;